// ── BulkAssignModal ────────────────────────────────────────────────────────────
// Assigns a list of controls (from checkbox selection) to a single assignee.
// Max 500 controls enforced server-side; UI disables Confirm beyond that.

import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBulkAssign } from './scope-api';

interface BulkAssignModalProps {
  controlIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export function BulkAssignModal({ controlIds, onClose, onSuccess }: BulkAssignModalProps) {
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [notes, setNotes] = useState('');
  const bulkMutation = useBulkAssign();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!assigneeEmail.trim()) { toast.error('Assignee email required'); return; }
    if (controlIds.length > 500) { toast.error('Maximum 500 controls per bulk operation'); return; }

    bulkMutation.mutate(
      {
        control_ids: controlIds,
        assignee_email: assigneeEmail.trim(),
        notes: notes || undefined,
      },
      {
        onSuccess: (r) => {
          toast.success(`${r.created} assignment${r.created !== 1 ? 's' : ''} created`);
          onSuccess();
          onClose();
        },
        onError: () => toast.error('Bulk assignment failed'),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-xl">
        <h2 className="text-base font-semibold text-text-primary mb-1">
          Assign {controlIds.length} control{controlIds.length !== 1 ? 's' : ''}
        </h2>
        <p className="text-xs text-text-secondary mb-4">
          Creates explicit (L3) assignments for each selected control.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Assignee email</Label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
              required
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
              placeholder="Add context for this bulk assignment"
            />
          </div>

          {controlIds.length > 500 && (
            <p className="text-xs text-accent-rose">
              Maximum 500 controls per bulk assignment. Please reduce your selection.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={bulkMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkMutation.isPending || !assigneeEmail.trim() || controlIds.length > 500}
            >
              {bulkMutation.isPending ? 'Assigning…' : `Assign ${controlIds.length}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
